/**
 * SB2-W-15 — In-app notification queries + mutations.
 *
 * Migrated to the new `notifications/v1` domain per OQ-SB2-B2(b)
 * (relocated out of `auth/v1` because notifications are a
 * cross-cutting workflow concern, not authentication). The new wire:
 *   - GET    /api/notifications/v1/notifications              listNotifications (cursor)
 *   - GET    /api/notifications/v1/notifications/unread-count getNotificationsUnreadCount
 *   - PATCH  /api/notifications/v1/notifications/{id}/read    markNotificationRead
 *   - DELETE /api/notifications/v1/notifications/{id}         deleteNotification
 *
 * The old `read-all` bulk endpoint isn't in the 1.0.0 contract — the
 * drawer iterates client-side. (We can add the bulk endpoint in a
 * future minor bump if the iterate-cost becomes a measurable issue;
 * for now the drawer rarely shows > 50 entries.)
 *
 * The NotificationBell polls `unread-count` every 30s. The drawer
 * lazily fetches the paged list on open.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "@aeriosiq/auth-client";
import { notificationsFetch } from "./notificationsFetch.js";

export type NotificationKind =
  | "data_acquisition.assigned"
  | "data_acquisition.auto_assigned"
  | "data_acquisition.unassigned"
  | "data_acquisition.claimed"
  | "assignment.role_change"
  | "system.message"
  /** DI-3-S68 (notifications/v1 1.1.0) — an intake set became stalled. */
  | "intake.stalled";

export interface NotificationPayload {
  dataAcquisitionId?: string;
  byUserId?: string | null;
  byDisplayName?: string | null;
  reason?:
    | "manual"
    | "auto_assign"
    | "self_claim"
    | "state_change_prescreening_complete";
  /** DI-3-S68 `intake.stalled` — the tenant the set lives in (its X-Tenant-ID). */
  tenantCode?: string;
  stalledSince?: string;
  stalledReason?: "no_progress" | "run_dead" | null;
  turbine?: string | null;
  park?: string | null;
  [k: string]: unknown;
}

export interface NotificationItem {
  id: string;
  kind: NotificationKind;
  payload: NotificationPayload;
  createdAt: string;
  readAt: string | null;
}

export interface NotificationListResponse {
  items: NotificationItem[];
  nextCursor: string | null;
  unreadCount: number;
}

export interface UnreadCountResponse {
  unreadCount: number;
}

export interface MarkReadResponse {
  id: string;
  readAt: string;
}

export const notificationKeys = {
  all: ["notifications"] as const,
  lists: () => [...notificationKeys.all, "list"] as const,
  list: (opts: { unreadOnly?: boolean; pageSize?: number }) =>
    [...notificationKeys.lists(), opts] as const,
  unreadCount: () => [...notificationKeys.all, "unread-count"] as const,
};

/** SB2-W-15 — polling cadence. 30s while visible, paused while hidden. */
const POLL_INTERVAL_MS = 30_000;

export function useNotifications(
  opts: { unreadOnly?: boolean; pageSize?: number } = {},
) {
  const pageSize = opts.pageSize ?? 50;
  const search = new URLSearchParams();
  if (opts.unreadOnly === true) search.set("unread_only", "true");
  search.set("pageSize", String(pageSize));
  // 2026-06-04 — on a deep-link boot these fired BEFORE tenant-from-host
  // resolved, so the X-Tenant-ID header was missing → 400 error toasts on
  // every fresh page load. Gate on the resolved tenant; the query starts
  // the moment the store hydrates.
  const tenantReady = useAuthStore((s) => s.tenant != null);
  return useQuery<NotificationListResponse>({
    queryKey: notificationKeys.list(opts),
    enabled: tenantReady,
    queryFn: () =>
      notificationsFetch<NotificationListResponse>(
        `/notifications?${search.toString()}`,
      ),
    staleTime: 10_000,
  });
}

export function useUnreadCount() {
  // Same tenant gate as useNotifications (2026-06-04 boot-race 400s).
  const tenantReady = useAuthStore((s) => s.tenant != null);
  return useQuery<UnreadCountResponse>({
    queryKey: notificationKeys.unreadCount(),
    enabled: tenantReady,
    queryFn: () =>
      notificationsFetch<UnreadCountResponse>("/notifications/unread-count"),
    // SB2-W-15 — poll once per 30s. Pauses while the tab is hidden
    // (TanStack default `refetchOnWindowFocus` semantics); resumes on
    // visibility change.
    refetchInterval: POLL_INTERVAL_MS,
    refetchOnWindowFocus: true,
    staleTime: POLL_INTERVAL_MS - 1_000,
  });
}

export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation<MarkReadResponse, Error, string>({
    mutationFn: (id) =>
      notificationsFetch<MarkReadResponse>(
        `/notifications/${encodeURIComponent(id)}/read`,
        { method: "PATCH" },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

/**
 * Client-side "mark all read" — iterate over the unread list and
 * PATCH each. The 1.0.0 contract doesn't expose a bulk endpoint;
 * the drawer rarely shows > 50 unreads so the iterate-cost is bounded.
 */
export function useMarkAllRead() {
  const qc = useQueryClient();
  return useMutation<{ markedReadCount: number }>({
    mutationFn: async () => {
      // Pull the current unread list, then mark each.
      const list = await notificationsFetch<NotificationListResponse>(
        "/notifications?unread_only=true&pageSize=200",
      );
      let count = 0;
      for (const item of list.items) {
        try {
          await notificationsFetch<MarkReadResponse>(
            `/notifications/${encodeURIComponent(item.id)}/read`,
            { method: "PATCH" },
          );
          count += 1;
        } catch {
          // best-effort per row
        }
      }
      return { markedReadCount: count };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

/**
 * Dismiss/delete a notification. Idempotent — duplicate dismiss is a
 * 204.
 */
export function useDeleteNotification() {
  const qc = useQueryClient();
  return useMutation<undefined, Error, string>({
    mutationFn: async (id) => {
      await notificationsFetch(`/notifications/${encodeURIComponent(id)}`, {
        method: "DELETE",
      });
      return undefined;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}
